<script setup>
// 미디어 허브 카드 한 장 — 어두운 시안(2026-10-06 전면 개편).
// Figma GOd8Fzt0RMbHTHo93Uqe3l / 메인 10004:1814 · 상세 10004:1426
//
// 메인과 상세가 같은 카드를 쓴다. 폭만 다르다 — 메인 376, 상세 350.
// 썸네일 비율은 둘 다 같아서(376/250 = 1.504, 350/233 = 1.502) 비율로 잡고
// 폭은 부모가 정하게 둔다.
//
//   썸네일   가로:세로 376:250, 모서리 20, object-cover
//   30 띄움
//   글 묶음  위아래 25 띄움
//     위    카테고리(주황 13 semibold) · 날짜(#7c7e83 13 regular) 한 줄, 5 띄우고
//           제목 18/26 semibold 흰색 두 줄 말줄임
//     아래  아이콘 18 + 출처 13 medium #7c7e83
//
// 글자는 원본 px 고정이다. 페이지 배율(zoom)이 통째로 줄이므로 여기서 또 줄이면
// 두 번 줄어든다 — media2 카드와 같은 규칙이다.
//
// ※ 출처 아이콘은 시안에 **같은 모양에 색만 다른 네 가지**가 있다
//   (#FF9401 · #01BBFF · #8FD762 · #A48F39). 어느 출처가 어느 색인지는 시안만으로
//   알 수 없어 가장 많이 쓰인 주황 하나로 두었다. 매칭표를 받으면 SRC_ICON 만 늘리면 된다.
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { FALLBACK_THUMB, formatDate, sourceLabel } from '@/composables/useMediaCards.js'
import {
  HOVER_DELAY,
  NO_PREVIEW_NOTE,
  PREVIEW_MS,
  playWhenReady,
  videoEmbed,
} from '@/composables/useVideoPreview.js'
import { getMediaHubContentGoUrl } from '@/api/mediaHub'
import imgSourceDot from '@/assets/figma/mh3/src-dot-1.svg'
import imgChevronLeft from '@/assets/figma/mh3/chevron-left.svg'

const props = defineProps({
  item: { type: Object, required: true },
  // 제목 줄 수. 시안은 두 줄이고 아랫줄이 어긋나지 않게 높이를 못 박는다.
  titleLines: { type: Number, default: 2 },
  // 마우스를 올리면 영상을 잠깐 틀지. 메인만 켠다(넷플릭스 컨셉, 2026-10-07).
  // 목록 화면은 끈 채로 둔다 - 한 화면에 100장이라 다 틀 수 있게 하면 산만하다.
  preview: { type: Boolean, default: false },
  // 올리면 카드가 커질지. 커지는 자리는 줄(MediaRow)이 위아래 여백으로 비워 둔다 —
  // 그 여백 없이 켜면 잘린다.
  zoomOnHover: { type: Boolean, default: false },
})

const thumb = computed(() => props.item.thumbnailUrl || FALLBACK_THUMB)

// 원문으로 갈 때는 서버의 /go 를 거친다 — 거기서 조회수가 올라간다(302 로 원문에 보낸다).
// originalUrl 로 바로 가면 통계가 안 잡힌다. 예전 화면들(HomeView ·
// MediaCategoryListView · MediaSearchView)이 쓰던 방식과 같다.
// 코드가 없으면 그때만 원문으로 바로 간다.
const goUrl = computed(() =>
  props.item?.homepageContentCode
    ? getMediaHubContentGoUrl(props.item.homepageContentCode)
    : props.item?.originalUrl || '',
)
const badge = computed(() => sourceLabel(props.item))
const date = computed(() => formatDate(props.item.publishedDt || props.item.fetchedDt))
const author = computed(
  () =>
    props.item.authorName ||
    props.item.mediaSourceName ||
    props.item.mediaHubCategoryName ||
    '',
)

// 글 길이가 제각각이라 줄 수를 못 박지 않으면 카드마다 높이가 달라져
// 아랫줄(출처)이 어긋난다.
const titleStyle = computed(() => ({
  WebkitLineClamp: String(props.titleLines),
  minHeight: `${26 * props.titleLines}px`,
}))

// 원본이 사라졌거나 외부 도메인이 막혀도 빈 칸이 남지 않게.
const onThumbError = (e) => {
  if (e.target.src.endsWith(FALLBACK_THUMB)) return
  e.target.src = FALLBACK_THUMB
}

// ── 올려 두면 잠깐 트는 미리보기 ──────────────────────────────────────
// 영상이 아닌 것(블로그·뉴스)은 embed 가 null 이라 아무 일도 안 일어난다.
const embed = computed(() => (props.preview ? videoEmbed(props.item) : null))
const playing = ref(false)
const open = ref(false)
const frame = ref(null)

// 커지는 것은 트는 것보다 먼저다. 넷플릭스도 먼저 커지고 조금 뒤에 영상이 돈다.
const ZOOM_DELAY = 300

let zoomTimer = 0
let enterTimer = 0
let stopTimer = 0
let cancelPlay = null

const clearAll = () => {
  window.clearTimeout(zoomTimer)
  window.clearTimeout(enterTimer)
  window.clearTimeout(stopTimer)
  cancelPlay?.()
  cancelPlay = null
}

const stop = () => {
  clearAll()
  playing.value = false
  open.value = false
}

// 영상만 끄고 커진 것은 둔다. 10초가 지나도 마우스가 있으면 커진 채로 남는다.
const stopVideo = () => {
  window.clearTimeout(stopTimer)
  cancelPlay?.()
  cancelPlay = null
  playing.value = false
}

const onEnter = () => {
  if (!embed.value && !props.zoomOnHover) return
  clearAll()
  if (props.zoomOnHover) {
    zoomTimer = window.setTimeout(() => {
      open.value = true
    }, ZOOM_DELAY)
  }
  // 네이버TV 는 자동재생이 안 된다. 플레이어를 띄우지 않고 썸네일 + 안내로 둔다.
  if (!embed.value?.playable) return
  // 스쳐 지나가는 것으로는 안 켜진다.
  enterTimer = window.setTimeout(() => {
    playing.value = true
    stopTimer = window.setTimeout(stopVideo, PREVIEW_MS)
    // autoplay=1 만으로는 안 도는 경우가 있어 유튜브에는 한 번 더 일러 준다.
    if (embed.value?.kind === 'youtube') {
      nextTick(() => {
        cancelPlay = playWhenReady(frame.value)
      })
    }
  }, HOVER_DELAY)
}

onBeforeUnmount(clearAll)
</script>

<template>
  <article
    class="mc-card flex w-full flex-col items-start gap-[30px] drop-shadow-[0px_2px_20px_rgba(0,0,0,0.08)]"
    :class="{ 'is-open': open }"
    @mouseenter="onEnter"
    @mouseleave="stop"
  >
    <div class="relative aspect-[376/250] w-full shrink-0 overflow-hidden rounded-[20px] bg-[#1c1c1c]">
      <img
        :src="thumb"
        :alt="item.title || ''"
        class="size-full object-cover"
        loading="lazy"
        @error="onThumbError"
      />
    </div>

    <div class="flex w-full shrink-0 flex-col items-start gap-[25px]">
      <div class="flex w-full shrink-0 flex-col items-start gap-[5px]">
        <div class="flex shrink-0 items-center gap-[10px] whitespace-nowrap text-[13px] not-italic leading-[18px]">
          <p v-if="badge" class="shrink-0 font-semibold text-[#ff9401]">{{ badge }}</p>
          <p v-if="date" class="shrink-0 font-normal text-[#7c7e83]">{{ date }}</p>
        </div>
        <p
          class="mc-title w-full shrink-0 overflow-hidden text-[18px] font-semibold not-italic leading-[26px] text-white"
          :style="titleStyle"
        >
          {{ item.title }}
        </p>
      </div>

      <div v-if="author" class="flex shrink-0 items-center gap-[5px]">
        <img alt="" class="block size-[18px] shrink-0 max-w-none" :src="imgSourceDot" />
        <p class="shrink-0 whitespace-nowrap text-[13px] font-medium not-italic leading-[18px] text-[#7c7e83]">
          {{ author }}
        </p>
      </div>
    </div>

    <!-- ── 올려 두면 뜨는 팝오버(넷플릭스식, 2026-10-07) ──────────────
         카드를 통째로 키우는 게 아니라 **커진 그림 + 그 아래 정보 판**이다.
         제자리 카드는 그대로 두고 그 위에 겹쳐 띄운다 — 줄의 배치가 안 흔들린다.
         자리: 그림을 원래 썸네일 한가운데에 맞춘다.
           폭 376 x 1.2 = 451,  그림 451 x 250/376 = 300,  위로 (300-250)/2 = 25
         좌우로 37.5 씩 넘치는데 줄의 안쪽 여백 60 이 받는다. -->
    <Transition name="mcpop">
      <div v-if="zoomOnHover && open" class="mc-pop-anchor">
        <div class="mc-pop">
          <div class="mc-pop-art">
        <img :src="thumb" :alt="item.title || ''" class="size-full object-cover" @error="onThumbError" />

        <!-- 미리보기. 소리는 꺼 둔다. 클릭은 카드가 받아야 하므로
             pointer-events 를 끈다 — 안 끄면 유튜브 플레이어가 클릭을 먹는다. -->
        <iframe
          v-if="playing && embed?.playable"
          ref="frame"
          :src="embed.src"
          class="mc-frame"
          title=""
          aria-hidden="true"
          tabindex="-1"
          allow="autoplay; encrypted-media; picture-in-picture"
          referrerpolicy="strict-origin-when-cross-origin"
        />
      </div>

      <div class="mc-pop-info">
        <p class="mc-pop-title">{{ item.title }}</p>

        <!-- 자동재생이 안 되는 곳(네이버TV)은 왜 안 도는지 알려 준다.
             플레이어를 띄우면 눌리지도 않는 재생 버튼만 남는다(iframe 은
             pointer-events 가 꺼져 있다). -->
        <p v-if="embed && !embed.playable" class="mc-pop-note">{{ NO_PREVIEW_NOTE }}</p>

        <div class="mc-pop-acts">
          <!-- 원문으로 바로. 카드 클릭(상세로 이동)과 겹치지 않게 막는다. -->
          <a
            v-if="goUrl"
            :href="goUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="mc-act mc-act-on"
            :aria-label="`${item.title || ''} 원문 보기`"
            @click.stop
          >
            <span class="mc-play" aria-hidden="true" />
          </a>
          <!-- 꺾쇠는 따로 안 받는다. 눌리면 카드로 올라가 상세로 간다. -->
          <span class="mc-act mc-act-more" aria-hidden="true">
            <img :src="imgChevronLeft" alt="" class="block size-[18px] -rotate-90" />
          </span>
        </div>

        <p class="mc-pop-meta">
          <span v-if="badge" class="text-[#ff9401]">{{ badge }}</span>
          <span v-if="date">{{ date }}</span>
          <span v-if="author">{{ author }}</span>
          </p>
        </div>
        </div>
      </div>
    </Transition>
  </article>
</template>

<style scoped>
/* 올려 두면 커진다(zoomOnHover). 넷플릭스 타일과 같은 결이다.
   커진 자리는 줄(MediaRow)이 위아래로 비워 둔다 — 그 여백이 없으면 잘린다.
   좌우는 줄의 안쪽 여백 60 이 받는다: 376 x 0.2 / 2 = 37.6 라 끝 칸도 안 넘친다.
   커진 카드는 좌우 띠(z-index 3)보다 위에 둬서 가려지지 않게 한다.

   배율은 1.2 다. 1.3 으로 해 봤더니 위로 59.5 자라서 바로 위 섹션 제목과
   탭 줄을 덮었다(2026-10-07). 줄 사이를 넷플릭스처럼 좁힌 것과 부딪친다 —
   1.2 면 위로 39.8 이라 제목까지 60 중 20 이 남는다. */
.mc-card {
  position: relative;
}
/* 팝오버가 떠 있는 동안은 이 카드가 이웃 위로 올라와야 한다. */
.mc-card.is-open {
  z-index: 5;
}

/* ── 팝오버는 "썸네일에서 자라난다" ────────────────────────────────
   넷플릭스는 툭 뜨지 않는다. 타일과 똑같은 크기에서 시작해 1.2 배로 자란다.
   그걸 그대로 하려고 두 겹으로 나눴다.

     mc-pop-anchor  썸네일과 **똑같은 상자**(inset 0, 같은 비율)다.
                    여기에만 scale 을 건다. 가운데를 기준으로 키우므로
                    그림이 썸네일 한가운데에서 사방으로 자란다.
     mc-pop         그 안에서 위에서부터 쌓인다. 그림은 상자를 꽉 채우고
                    정보 판은 아래로 삐져나온다 — 같이 커지고 같이 내려간다.

   이렇게 하면 px 을 안 박아도 된다. 목록 카드(350)에서도 저절로 맞는다.
   전에는 폭 451 · 위로 25 를 직접 박아서 카드 폭이 다르면 어긋났다. */
.mc-pop-anchor {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  aspect-ratio: 376 / 250;
  width: 100%;
  transform: scale(1.2);
  transform-origin: center center;
  will-change: transform;
}
/* 들고 날 때. 시작·끝은 타일과 똑같은 크기(scale 1)다. */
.mcpop-enter-active,
.mcpop-leave-active {
  transition: transform 320ms cubic-bezier(0.2, 0.7, 0.3, 1);
}
.mcpop-enter-from,
.mcpop-leave-to {
  transform: scale(1);
}
/* 정보 판은 조금 늦게 배어 나온다. 자라는 동안 글이 먼저 보이면 산만하다. */
.mc-pop-info {
  transition: opacity 180ms ease 70ms;
}
.mcpop-enter-from .mc-pop-info,
.mcpop-leave-to .mc-pop-info {
  opacity: 0;
}

.mc-pop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
  border-radius: 10px;
  background: #181818;
  box-shadow: 0 20px 40px rgb(0 0 0 / 60%);
}
.mc-pop-art {
  position: relative;
  aspect-ratio: 376 / 250;
  width: 100%;
  overflow: hidden;
  background: #1c1c1c;
}
.mc-pop-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}
.mc-pop-title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  color: #fff;
  word-break: break-word;
}
.mc-pop-acts {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* 동그란 버튼. 첫 칸만 흰 바탕이다(넷플릭스의 재생 버튼). */
.mc-act {
  display: flex;
  height: 36px;
  width: 36px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  box-shadow: inset 0 0 0 2px rgb(255 255 255 / 50%);
  transition: background-color 150ms ease;
}
.mc-act-on {
  background: #fff;
  box-shadow: none;
}
.mc-act-on:hover {
  background: #e0e0e0;
}
.mc-act-more {
  margin-left: auto;
}
/* 재생 삼각형. 아이콘 파일이 따로 없어 테두리로 그린다. */
.mc-play {
  display: block;
  height: 0;
  width: 0;
  margin-left: 3px;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 12px solid #000;
}
/* 미리보기가 안 되는 이유 한 줄. 메타보다 조금 옅게 둔다. */
.mc-pop-note {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #6a6c71;
}

/* 가운뎃점으로 이어 붙인 한 줄. */
.mc-pop-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #7c7e83;
}
.mc-pop-meta > span + span::before {
  content: '·';
  margin-right: 8px;
}

/* 미리보기 창. 영상은 16:9 인데 썸네일 칸은 376:250(1.504)이라 그냥 채우면
   위아래에 검은 띠가 생긴다. 세로를 맞추고 가로로 넘치게 둬서 꽉 채운다 —
   250 x 16/9 = 444.4 이고 칸 폭 376 의 118.2% 다. 칸 비율이 고정이라 이 값도 고정이다. */
.mc-frame {
  position: absolute;
  top: 0;
  left: 50%;
  height: 100%;
  width: 118.2%;
  translate: -50% 0;
  border: 0;
  pointer-events: none;
  animation: mc-fade 250ms ease both;
}
@keyframes mc-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 시안의 말줄임은 두 줄이다. -webkit-line-clamp 는 줄 수를 인라인 style 로 받는다. */
.mc-title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  word-break: break-word;
}
</style>
